/**
 * About — /admin/about. One place for what an operator asks about the copy
 * they run: its version, whether a newer release is out, the license it runs
 * under, and where to get help.
 *
 * The Version section is the existing `VersionInfo` panel, moved here
 * unchanged from the bottom of the Global Admin home page, where it sat under
 * a "Build" heading below the card grid. It stays behind the superadmin gate,
 * as before: /api/build-info is public, but the panel is for operators.
 *
 * The License section names the license and links to the docs License page.
 * It deliberately does not restate the terms: a summary here would be a
 * second copy that could drift from LICENSE, and the docs page already
 * builds its summary and full text from that one file. Every copy also
 * carries the text itself, in /app/LICENSE and, for a copy made with the
 * install command, in the install folder.
 *
 * Lives under pages/Admin/ beside the other Global Admin sections, one folder
 * each (Artifacts/, Config/), and is listed in `ADMIN_SECTIONS`, so the side
 * menu and the home page's grid both pick it up from there.
 */
import { VersionInfo } from '@/components/settings/VersionInfo'
import { DOCS_BASE_URL } from '@/help/HelpPanel'

const LICENSE_URL = `${DOCS_BASE_URL}/license/`
const SALES_EMAIL = 'sales@ottotester.ai'
const SUPPORT_EMAIL = 'support@ottotester.ai'

export function AboutPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">About</h1>
        <p className="text-sm text-muted-foreground mt-1">
          The version of OttoTester this copy runs, whether a newer release is out, the license it
          runs under, and where to get help.
        </p>
      </div>

      <section aria-labelledby="about-version">
        <h2 id="about-version" className="text-base font-semibold mb-3">
          Version
        </h2>
        <VersionInfo />
      </section>

      <section aria-labelledby="about-license" className="border-t pt-6">
        <h2 id="about-license" className="text-base font-semibold mb-3">
          License
        </h2>
        <p className="text-sm">OttoTester is licensed under the Business Source License 1.1.</p>
        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm">
          <a
            href={LICENSE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            Read the license
          </a>
          <a href={`mailto:${SALES_EMAIL}`} className="text-primary hover:underline">
            Ask about a license
          </a>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          The full text also ships with every copy, in a file named <code>LICENSE</code>. On a copy
          you installed with the install command, it&apos;s in your install folder.
        </p>
      </section>

      <section aria-labelledby="about-support" className="border-t pt-6">
        <h2 id="about-support" className="text-base font-semibold mb-3">
          Support
        </h2>
        <p className="text-sm">
          Questions, or a problem with this copy? Email{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline">
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
      </section>
    </div>
  )
}
